// LOT Traceability — 명세서 "5분 이내 역추적 100%" 목표 충족
import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { Skeleton } from '../components/Skeleton'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import { useGlobalReveal } from '../hooks/useReveal'
import { useT } from '../lib/i18n'

interface ProductLot {
  product_lot_id: string
  work_order_id: string
  item_code: string
  quantity: number
  produced_at: string
  equipment_id: string | null
  worker_id: string | null
  quality_status: 'pending' | 'pass' | 'fail' | 'hold'
}

interface TraceResult {
  lot: ProductLot & { line_id?: string; equipment_name?: string }
  parents: Array<{
    parent_lot_id: string
    parent_type: 'material' | 'product'
    consumed_qty: number
    recorded_at: string
    material_item_code?: string
    vendor?: string
    received_at?: string
    uom?: string
  }>
  inspections: Array<{
    inspection_id: string
    measurement_value: number
    pass_fail: 'Pass' | 'Fail'
    collected_at: string
  }>
  alarms: Array<{
    event_id: string
    category: string
    severity: 'critical' | 'high' | 'medium' | 'low'
    message: string
    raised_at: string
    resolved_at: string | null
  }>
}

const QUALITY_BADGE: Record<ProductLot['quality_status'], string> = {
  pending: '', pass: 'success', fail: 'danger', hold: 'warn',
}

export function LotTracePage() {
  useGlobalReveal()
  const toast = useToast()
  const { t } = useT()
  const [products, setProducts] = useState<ProductLot[] | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [trace, setTrace] = useState<TraceResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [query, setQuery] = useState('')

  useEffect(() => {
    api.get<ProductLot[]>('/lots/products')
      .then(setProducts)
      .catch((e) => { setProducts([]); toast.push('error', e?.message ?? '조회 실패') })
  }, [toast])

  const doTrace = async (id: string) => {
    setSelected(id)
    setBusy(true)
    setTrace(null)
    try {
      const t0 = performance.now()
      const t = await api.get<TraceResult>(`/lots/products/${id}/trace`)
      setTrace(t)
      toast.push('success', `역추적 완료 (${(performance.now() - t0).toFixed(0)}ms)`)
    } catch (e: any) {
      toast.push('error', e?.message ?? '추적 실패')
    } finally {
      setBusy(false)
    }
  }

  const filtered = (products ?? []).filter(
    (p) => !query || p.product_lot_id.includes(query) || p.item_code.includes(query),
  )

  return (
    <main id="main" className="container stack" style={{ paddingBlock: '2rem' }}>
      <h1 className="page-title reveal">
        <FontAwesomeIcon icon="sitemap" /> {t('lot.title')}
      </h1>
      <p className="muted reveal">
        {t('lot.intro')}
      </p>

      <Card glass tilt={false} spotlight={false} className="reveal" as="section">
        <div className="row" style={{ marginBottom: '1rem' }}>
          <div className="field" style={{ flex: '1 1 280px', marginBottom: 0 }}>
            <label htmlFor="lot-q">LOT/품번 검색</label>
            <input
              id="lot-q"
              placeholder="PL-2026-A100-01, ITEM-A100"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        {!products ? (
          <Skeleton h={120} />
        ) : filtered.length === 0 ? (
          <div className="empty-state" role="status">
            <FontAwesomeIcon icon="boxes-stacked" className="icon-lg" />
            <p className="muted" style={{ margin: 0 }}>일치하는 제품 LOT이 없습니다.</p>
          </div>
        ) : (
          <div className="table-wrap" style={{ '--table-min-w': '780px' } as React.CSSProperties}>
            <table className="table" aria-label="제품 LOT 목록">
              <thead>
                <tr>
                  <th>제품 LOT</th>
                  <th>품번</th>
                  <th>수량</th>
                  <th>작업지시</th>
                  <th>설비</th>
                  <th>생산시각</th>
                  <th>품질</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.product_lot_id} aria-selected={selected === p.product_lot_id}>
                    <td className="mono">{p.product_lot_id}</td>
                    <td>{p.item_code}</td>
                    <td className="mono">{p.quantity}</td>
                    <td className="mono">{p.work_order_id}</td>
                    <td className="mono">{p.equipment_id ?? '—'}</td>
                    <td>{new Date(p.produced_at).toLocaleString('ko-KR')}</td>
                    <td>
                      <span className={`badge ${QUALITY_BADGE[p.quality_status]}`}>
                        {p.quality_status}
                      </span>
                    </td>
                    <td>
                      <Button
                        variant="ghost"
                        magnetic={false}
                        onClick={() => doTrace(p.product_lot_id)}
                        disabled={busy && selected === p.product_lot_id}
                        aria-label={`${p.product_lot_id} 역추적`}
                      >
                        <FontAwesomeIcon icon={busy && selected === p.product_lot_id ? 'circle-notch' : 'arrows-turn-to-dots'} spin={busy && selected === p.product_lot_id} />
                        <span>역추적</span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {trace && (
        <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="trace-h">
          <h2 id="trace-h" style={{ fontSize: 'var(--fs-lg)' }}>
            <FontAwesomeIcon icon="link" /> 계보 {trace.lot.product_lot_id}
          </h2>

          <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', marginBlock: '0.6rem 1.2rem' }}>
            <div><span className="muted">품번</span> · <span className="mono">{trace.lot.item_code}</span></div>
            <div><span className="muted">수량</span> · <span className="mono">{trace.lot.quantity}</span></div>
            <div><span className="muted">설비</span> · <span className="mono">{trace.lot.equipment_id ?? '—'}</span> {trace.lot.equipment_name && <span>({trace.lot.equipment_name})</span>}</div>
            <div><span className="muted">라인</span> · <span className="mono">{trace.lot.line_id ?? '—'}</span></div>
            <div><span className="muted">작업자</span> · <span className="mono">{trace.lot.worker_id ?? '—'}</span></div>
            <div><span className="muted">생산</span> · {new Date(trace.lot.produced_at).toLocaleString('ko-KR')}</div>
          </div>

          <h3 style={{ fontSize: 'var(--fs-base)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-ink-3)' }}>
            소비된 자재 LOT ({trace.parents.length})
          </h3>
          {trace.parents.length === 0 ? (
            <p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>계보 정보 없음</p>
          ) : (
            <ul className="stack" style={{ paddingLeft: 0, listStyle: 'none', marginBlock: '0.4rem 1.2rem' }}>
              {trace.parents.map((p) => (
                <li key={p.parent_lot_id} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', padding: '0.5rem 0.75rem', borderRadius: 'var(--radius-sm)', background: 'var(--color-bg-soft)' }}>
                  <span className="badge info">{p.parent_type}</span>
                  <span className="mono">{p.parent_lot_id}</span>
                  <span className="muted">·</span>
                  <span>{p.material_item_code ?? ''}</span>
                  {p.vendor && <span className="muted">· {p.vendor}</span>}
                  <span style={{ marginLeft: 'auto' }} className="mono">{p.consumed_qty} {p.uom ?? ''}</span>
                </li>
              ))}
            </ul>
          )}

          <h3 style={{ fontSize: 'var(--fs-base)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-ink-3)' }}>
            검사 ({trace.inspections.length}) / 동시 알람 ({trace.alarms.length})
          </h3>
          <div className="row" style={{ alignItems: 'flex-start', gap: '1rem', marginTop: '0.4rem' }}>
            <div style={{ flex: '1 1 320px' }}>
              {trace.inspections.length === 0 ? (
                <p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>검사 기록 없음</p>
              ) : (
                <ul style={{ paddingLeft: 0, listStyle: 'none', display: 'grid', gap: '0.3rem' }}>
                  {trace.inspections.map((i) => (
                    <li key={i.inspection_id} className="mono" style={{ fontSize: 'var(--fs-sm)' }}>
                      <span className={`badge ${i.pass_fail === 'Pass' ? 'success' : 'danger'}`}>{i.pass_fail}</span>{' '}
                      {i.measurement_value} · {new Date(i.collected_at).toLocaleString('ko-KR')}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div style={{ flex: '1 1 320px' }}>
              {trace.alarms.length === 0 ? (
                <p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>동시 알람 없음</p>
              ) : (
                <ul style={{ paddingLeft: 0, listStyle: 'none', display: 'grid', gap: '0.3rem' }}>
                  {trace.alarms.map((a) => (
                    <li key={a.event_id} style={{ fontSize: 'var(--fs-sm)' }}>
                      <span className={`badge ${a.severity === 'critical' ? 'danger' : a.severity === 'high' ? 'warn' : 'info'}`}>
                        {a.severity}
                      </span>{' '}
                      <span className="mono">{a.category}</span> · {a.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Card>
      )}
    </main>
  )
}
